import React, { useState, useRef, useCallback} from "react";
import { Button, CircularProgress } from "@mui/material";
import { TextField, Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete } from "@mui/material";
import { useEffect } from "react";
import { apiClient } from "../../../../globals/utils/api";
import { toast } from "react-toastify";
import { loadGoogleMaps } from "../../../../globals/utils/googleMapsLoader";


// Google Autocomplete Component
const GoogleAutoComplete = ({ placeholder, callback }) => {
  const autoCompleteRef = useRef();
  const inputRef = useRef();
  const [loaded, setLoaded] = useState(false);

  const loadGoogleMapsScript = useCallback(() => {
    loadGoogleMaps()
      .then(() => setLoaded(true))
      .catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    loadGoogleMapsScript();
  }, [loadGoogleMapsScript]);

  useEffect(() => {
    if (loaded && window.google) {
      autoCompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        { fields: ["address_components"], types: ["geocode"] }
      );
  
      autoCompleteRef.current.addListener("place_changed", () => {
        const place = autoCompleteRef.current.getPlace();
  
        if (!place || !place.address_components) {
          console.error("No valid place returned or missing address components.");
          return;
        }
  
        let address = "", city = "", state = "", zipCode = "";
  
        place.address_components.forEach((component) => {
          const type = component.types[0];
          switch (type) {
            case "street_number":
              address = `${component.long_name} ${address}`;
              break;
            case "route":
              address += component.long_name;
              break;
            case "locality":
              city = component.long_name;
              break;
            case "administrative_area_level_1":
              state = component.long_name;
              break;
            case "postal_code":
              zipCode = component.long_name;
              break;
            default:
              break;
          }
        });

  
        if (callback) {
          callback(address, city, state, zipCode);
        }
      });
    }
  }, [loaded, callback]);
  

  return (
    <>
    <TextField
  variant="outlined"
  fullWidth
  placeholder={placeholder}
  inputRef={inputRef}
  sx={{ marginBottom: "8px" }} // Add margin-bottom
/>
      {/* Ensure Google Places dropdown appears above MUI Dialogs */}
      <style>{`
        .pac-container { 
          z-index: 99999 !important;
        }
      `}</style>
    </>
  );
};

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth", // Smooth scrolling
  });
};

export const ObligeeInformationForm = ({ globalData, data, updateData, role, isDisabled, bondType, suppressDefaultObligee = false }) => {
    const [obligees, setObligees] = useState([]);
    const [loadingObligees, setLoadingObligees] = useState(false);
    const [openObligeeDialog, setOpenObligeeDialog] = useState(false);
    const [newObligee, setNewObligee] = useState({
      name: "",
      street: "",
      city: "",
      state: "",
      zip: ""
    });
    const [allCities, setAllCities] = useState([]);
    const [allStates, setAllStates] = useState([]);
    const dataRef = useRef(data);
    dataRef.current = data;

    const normalizeId = (v) => (v === null || v === undefined ? "" : String(v));
    const selectedObligee = obligees.find(
      (o) => normalizeId(o.obligee_id) === normalizeId(data?.obligeeId)
    );

    // [Obligee Retrieve] Log so we can confirm no defaulting on retrieve
    if (typeof window !== "undefined" && suppressDefaultObligee) {
      console.log("[ObligeeInformationForm retrieve] suppressDefaultObligee=true, data.obligeeId=", data?.obligeeId, "data=", data);
    }

    // Fetch obligees and dropdown options. On retrieve (suppressDefaultObligee), never set default — parent is sole source.
    useEffect(() => {
      const fetchData = async () => {
        setLoadingObligees(true);
        try {
          const [obligeesRes, citiesRes, statesRes] = await Promise.all([
            apiClient("/obligee-list-create/"),
            apiClient("/fields/city/"),
            apiClient("/fields/states/")
          ]);
          
          setObligees(obligeesRes);
          setAllCities(citiesRes);
          setAllStates(statesRes);

          if (suppressDefaultObligee) {
            console.log("[ObligeeInformationForm retrieve] Skipping default obligee (retrieve page); parent supplies obligee from API.");
            return;
          }

          // Find and set default obligee only when not on retrieve and not already set
          const current = dataRef.current;
          if (!current?.obligeeId || String(current.obligeeId).trim() === "") {
            const defaultObligeeName = bondType === "VA" 
              ? "Secretary, U.S. Department of Veterans Affairs"
              : "Governor of Florida (Ron Desantis)";
            const defaultObligee = obligeesRes.find(o => 
              o.obligee_name === defaultObligeeName
            );
            if (defaultObligee) {
              console.log("[ObligeeInformationForm create] Setting default obligee:", defaultObligee.obligee_id, defaultObligeeName);
              updateData({ ...current, obligeeId: defaultObligee.obligee_id });
            }
          }
        } catch (error) {
          console.error("Error loading data:", error);
          toast.error("Failed to load obligee data");
        } finally {
          setLoadingObligees(false);
        }
      };
      
      fetchData();
      scrollToTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  
    const handleGoogleCallback = (address, city, state, zip) => {
      setNewObligee(prev => ({
        ...prev,
        street: address,
        city: allCities.find(c => c.toLowerCase() === city.toLowerCase()) || city,
        state: allStates.find(s => s.toLowerCase() === state.toLowerCase()) || state,
        zip
      }));
    };
  
    const handleAddObligee = async () => {
      try {
        setLoadingObligees(true);
        const response = await apiClient("/obligee-list-create/", "POST", {
          obligee_name: newObligee.name,
          obligee_address: newObligee.street,
          obligee_city: newObligee.city,
          obligee_state: newObligee.state,
          obligee_zip: newObligee.zip
        });
        
        // Update the obligee list and select the new one
        setObligees(prev => [...prev, response]);
        updateData({ ...data, obligeeId: response.obligee_id });
        setOpenObligeeDialog(false);
        setNewObligee({ name: "", street: "", city: "", state: "", zip: "" });
        toast.success("Obligee added successfully!");
      } catch (error) {
        console.error("Error adding obligee:", error);
        toast.error("Failed to add obligee");
      } finally {
        setLoadingObligees(false);
      }
    };
  
    return (
      <Box className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8" sx={{ border: "1px solid #e0e0e0" }}>
        <Typography variant="h5" className="font-bold mb-4">Obligee Information</Typography>
  
        <Box className="grid grid-cols-1 gap-4">
          <Autocomplete
          disabled={isDisabled}
            options={obligees}
            getOptionLabel={(option) => option.obligee_name || ""}
            loading={loadingObligees}
            value={selectedObligee || null}
            isOptionEqualToValue={(option, value) =>
              normalizeId(option?.obligee_id) === normalizeId(value?.obligee_id)
            }
            onChange={(event, newValue) => {
              updateData({ ...data, obligeeId: newValue ? newValue.obligee_id : null });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                disabled={isDisabled}
                label="Select Obligee"
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingObligees ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
  
          <Button 
            variant="outlined" 
            onClick={() => setOpenObligeeDialog(true)}
            disabled={loadingObligees || isDisabled}
          >
            {loadingObligees ? <CircularProgress size={24} /> : 'Add New Obligee'}
          </Button>
  
          {data?.obligeeId && (
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              border: "1px solid #e0e0e0", 
              borderRadius: 2,
              backgroundColor: "#f9f9f9"
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Selected Obligee:</Typography>
              {(() => {
                const selected = selectedObligee;
                return selected ? (
                  <>
                    <Typography sx={{ mb: 1 }}>
                      <Box component="span" sx={{ fontWeight: 'bold' }}>Name:</Box> {selected.obligee_name}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <Box component="span" sx={{ fontWeight: 'bold' }}>Address:</Box> {selected.obligee_address}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <Box component="span" sx={{ fontWeight: 'bold' }}>City:</Box> {selected.obligee_city}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <Box component="span" sx={{ fontWeight: 'bold' }}>State:</Box> {selected.obligee_state}
                    </Typography>
                    <Typography>
                      <Box component="span" sx={{ fontWeight: 'bold' }}>ZIP:</Box> {selected.obligee_zip}
                    </Typography>
                  </>
                ) : null;
              })()}
            </Box>
          )}
        </Box>
  
        {/* Add New Obligee Dialog */}
        <Dialog open={openObligeeDialog} onClose={() => setOpenObligeeDialog(false)} fullWidth maxWidth="sm">
          <DialogTitle>Add New Obligee</DialogTitle>
          <DialogContent>
            <TextField
              label="Obligee Name"
              value={newObligee.name}
              onChange={(e) => setNewObligee({...newObligee, name: e.target.value})}
              fullWidth
              margin="normal"
              required
              sx={{ mb: 2 }}
            />
            
            <GoogleAutoComplete 
              placeholder="Search Address" 
              callback={handleGoogleCallback} 
            />
            
            <TextField
              label="Street Address"
              value={newObligee.street}
              onChange={(e) => setNewObligee({...newObligee, street: e.target.value})}
              fullWidth
              margin="normal"
              required
              sx={{ mb: 2 }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Autocomplete
                options={allCities}
                value={newObligee.city}
                onChange={(e, value) => setNewObligee({...newObligee, city: value})}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="City" 
                    fullWidth 
                    required 
                  />
                )}
                sx={{ flex: 1 }}
              />
              
              <Autocomplete
                options={allStates}
                value={newObligee.state}
                onChange={(e, value) => setNewObligee({...newObligee, state: value})}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="State" 
                    fullWidth 
                    required 
                  />
                )}
                sx={{ flex: 1 }}
              />
            </Box>
            
            <TextField
              label="ZIP Code"
              value={newObligee.zip}
              onChange={(e) => setNewObligee({...newObligee, zip: e.target.value})}
              fullWidth
              margin="normal"
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenObligeeDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAddObligee} 
              variant="contained" 
              disabled={!newObligee.name || !newObligee.street || !newObligee.city || !newObligee.state || !newObligee.zip}
            >
              Add Obligee
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };